import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { DateRange, MatCalendar } from '@angular/material/datepicker';
import * as moment from 'moment';
import { CalendarViewData } from './../model/calendar-view-data';

@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class CalendarComponent implements OnInit, AfterViewInit {
  firstCalendarViewData!: CalendarViewData;
  secondCalendarViewData!: CalendarViewData;
  @Input() selectedDates!: DateRange<Date>;
  @Input() minDate!: Date;
  @Input() maxDate!: Date;
  @Output() selectionChanged: EventEmitter<DateRange<Date>> = new EventEmitter();

  @ViewChild('firstCalendarView') firstCalendarView!: MatCalendar<Date>;
  @ViewChild('secondCalendarView') secondCalendarView!: MatCalendar<Date>;

  moment = moment;
  constructor(private cdref: ChangeDetectorRef, private el: ElementRef, private renderer: Renderer2) {}

  ngOnInit(): void {
    this.initFirstCalendar();
    this.initSecondCalendar();
  }

  ngAfterViewInit(): void {
    this.addFirstCalendarButtonEvents();
    this.addSecondCalendarButtonEvents();
  }

  secondViewMonthSelected() {
    this.removeDefaultFocus(this);
  }

  firstCalendarYearSelected(event: Date) {
    this.firstCalendarView._goToDateInView(moment(event) as any, 'year');
  }

  firstCalendarMonthSelected(event: Date) {
    const nextMonthDate = this.getFirstDateOfNextMonth(moment(event));
    this.firstCalendarView._goToDateInView(moment(event) as any, 'month');
    this.secondCalendarView._goToDateInView(moment(nextMonthDate) as any, 'month');
  }

  secondCalendarYearSelected(event: Date) {
    this.secondCalendarView._goToDateInView(moment(event) as any, 'year');
  }

  secondCalendarMonthSelected(event: Date) {
    const previousMonthDate = this.getFirstDateOfPreviousMonth(moment(event));
    this.firstCalendarView._goToDateInView(moment(previousMonthDate) as any, 'month');
    this.secondCalendarView._goToDateInView(moment(event) as any, 'month');
  }

  updateDateRangeSelection(date: Date | null): void {
    const selectedDates = this.selectedDates;
    if (
      !selectedDates ||
      !selectedDates.start ||
      (selectedDates.start && selectedDates.end) ||
      (selectedDates.start && date && selectedDates.start > date)
    ) {
      this.selectedDates = new DateRange<Date>(date, null);
      if (moment(date).get('month') !== moment(this.firstCalendarViewData.startDate).get('month')) {
        this.firstCalendarViewData.startDate = moment(date) as any;
        this.cdref.markForCheck();
      }
      const secondCalendarStartDate = moment(date).add(1, 'month');
      this.secondCalendarViewData.startDate = secondCalendarStartDate as any;
    } else {
      this.selectedDates = new DateRange<Date>(selectedDates.start, date);
    }
    this.cdref.markForCheck();
    this.selectionChanged.emit(this.selectedDates);
  }

  private handleFirstCalDatePrevEvent(classRef: CalendarComponent): void {
    const leftDateCalender = classRef.firstCalendarView;
    if (leftDateCalender.currentView.toLocaleLowerCase() === 'month') {
      const date: Date = new Date(leftDateCalender['_clampedActiveDate']);
      const nextMonthDate = classRef.getFirstDateOfNextMonth(date);
      // classRef.secondCalendarView.minDate = nextMonthDate;
      classRef.secondCalendarView._goToDateInView(moment(nextMonthDate) as any, 'month');
      classRef.removeDefaultFocus(classRef);
      classRef.cdref.markForCheck();
    }
  }

  private handleSecondCalDateNextEvent(classRef: CalendarComponent): void {
    const secondDateCalender = classRef.secondCalendarView;
    if (secondDateCalender.currentView.toLocaleLowerCase() === 'month') {
      const date: Date = new Date(secondDateCalender['_clampedActiveDate']);
      const previousMonthDate = classRef.getFirstDateOfPreviousMonth(date);
      classRef.firstCalendarView._goToDateInView(moment(previousMonthDate) as any, 'month');
      classRef.removeDefaultFocus(classRef);
      classRef.cdref.markForCheck();
    }
  }

  removeDefaultFocus(classRef: CalendarComponent): void {
    setTimeout(() => {
      const btn: HTMLButtonElement[] = classRef.el.nativeElement.querySelectorAll(
        '#secondCalendarView button.mat-calendar-body-active'
      );
      if (btn?.length) {
        btn[0].blur();
      }
    }, 1);
  }

  private addFirstCalendarButtonEvents(): void {
    const monthPrevBtn = this.el.nativeElement.querySelectorAll('#firstCalendarView .mat-calendar-previous-button');
    this.attachClickEvent(monthPrevBtn, this.handleFirstCalDatePrevEvent);
  }

  private addSecondCalendarButtonEvents(): void {
    const monthNextBtn = this.el.nativeElement.querySelectorAll('#secondCalendarView .mat-calendar-next-button');
    this.attachClickEvent(monthNextBtn, this.handleSecondCalDateNextEvent);
  }

  private attachClickEvent(nodes: any, handler: any): void {
    if (!nodes) {
      return;
    }
    Array.from(nodes).forEach(button => {
      this.renderer.listen(button, 'click', () => {
        handler(this);
      });
    });
  }

  private initFirstCalendar(): void {
    this.firstCalendarViewData = new CalendarViewData();
    this.firstCalendarViewData.startDate = (this.selectedDates?.start || moment()) as any;
  }

  private initSecondCalendar(): void {
    const firstCalendarStartDate = this.selectedDates?.start ? moment(this.selectedDates.start) : moment();
    this.secondCalendarViewData = new CalendarViewData();
    this.secondCalendarViewData.minDate = this.selectedDates.start;
    const secondCalendarStartDate = moment(firstCalendarStartDate).add(1, 'month');
    this.secondCalendarViewData.startDate = (
      this.selectedDates?.end ? this.selectedDates.end : secondCalendarStartDate
    ) as any;
    if (
      moment(this.firstCalendarViewData.startDate).get('month') ===
      moment(this.secondCalendarViewData.startDate).get('month')
    ) {
      this.secondCalendarViewData.startDate = moment(firstCalendarStartDate).add(1, 'month') as any;
    }
  }

  private getFirstDateOfNextMonth(date: Date | moment.Moment): any {
    return moment(date).add(1, 'month').startOf('month');
  }

  private getFirstDateOfPreviousMonth(date: Date | moment.Moment): any {
    return moment(date).subtract(1, 'month').startOf('month');
  }
}
